// src/components/AgentCommandCenter/CallConsole.js
import React from 'react';
import { Mic, MicOff, MonitorPlay, MonitorPause } from 'lucide-react';
import { Button } from 'common/components';
import './CallConsole.css';
import { QualificationBadges, UpdateContactStatus } from '../AgentCallConsole/components';
import { AggregatedQualificationsAccordion } from 'components/AggregatedQualificationsAccordion';

const CallingCard = ({
    callStatus,
    onAcceptCall,
    onRejectCall,
    onHangupCall,
    onMuteToggle,
    isMuted,
    onHoldToggle,
    isOnHold,
    isActive,
}) => {
    // Determine what buttons to show based on call status
    const renderCallButtons = () => {
        switch (callStatus) {
            case 'incoming':
                return (
                    <div className='calling-card-button-container'>
                        <button className='calling-card-buttons calling-btn-answer' onClick={onAcceptCall}>
                            Answer
                        </button>
                        <button className='calling-card-buttons calling-btn-decline' onClick={onRejectCall}>
                            Decline
                        </button>
                    </div>
                );
            case 'in-progress':
                return (
                    <div className='calling-card-button-container'>
                        <button className='calling-card-buttons calling-btn-decline' onClick={onHangupCall}>
                            Hang Up
                        </button>
                    </div>
                );
            case 'ready':
            default:
                return <div className='calling-status'>{/*<p>Ready for calls</p>*/}</div>;
        }
    };

    return (
        <div className='calling-card-container'>
            {/* Display call status */}
            <div className={`agent-call-status ${callStatus} ${isActive ? 'online' : 'offline'}`}>
                {callStatus === 'incoming' && <span>Incoming Call...</span>}
                {callStatus === 'in-progress' && <span>Call in Progress</span>}
                {callStatus === 'ready' && isActive && <span>Ready for Calls</span>}
                {callStatus === 'ready' && !isActive && <span>Agent Offline</span>}
                {callStatus === 'idle' && <span>Initializing...</span>}
                {callStatus === 'error' && <span>Error with Twilio</span>}
                {callStatus === 'initializing' && <span>Agent Offline</span>}
            </div>

            {/* Call control buttons */}
            {renderCallButtons()}

            {/* Call action buttons (like mute) - only show during active call */}
            {callStatus === 'in-progress' && (
                <div className='call-action-container'>
                    <div className='call-action-button'>
                        <Button
                            type='button'
                            variant='outline-secondary'
                            icon={isMuted ? Mic : MicOff}
                            onClick={onMuteToggle}
                            iconOnly
                            isRounded
                            size='lg'
                        />
                        {isMuted ? 'Unmute' : 'Mute'}
                    </div>
                    <div className='call-action-button'>
                        <Button
                            type='button'
                            variant='outline-secondary'
                            icon={isOnHold ? MonitorPause : MonitorPlay}
                            onClick={onHoldToggle}
                            size='lg'
                            iconOnly
                            isRounded
                        />
                        {isOnHold ? 'Unhold' : 'Hold'}
                    </div>
                </div>
            )}

            {/* Empty task status div - span removed as requested */}
            <div className='task-status'></div>
        </div>
    );
};

const ClientInfoCard = ({ client }) => (
    <div className='client-info-card'>
        <h3>Client Information</h3>
        <div className='client-info-container'>
            <div className='client-info-field-container'>
                <p>Name:</p>
                <p>{client.name}</p>
            </div>
            <div className='client-info-field-container'>
                <p>Phone Number:</p>
                <p>{client.phoneNumber}</p>
            </div>
            <div className='client-info-field-container'>
                <p>Workflow:</p>
                <p>{client.workflow}</p>
            </div>
        </div>
    </div>
);

const CallTranscript = ({ messages }) => (
    <div className='transcript-section'>
        <h3>Call Transcript</h3>
        <div className='transcript-messages'>
            {messages.map((msg, i) => (
                <div key={i} className={`msg-row ${msg.sender === 'SLAI Agent' ? 'agent' : 'client'}`}>
                    <p className='sender'>{msg.sender}</p>
                    <div className='msg-bubble'>{msg.text}</div>
                </div>
            ))}
        </div>
    </div>
);

const CallConsole = ({
    client,
    qualifications,
    aggregatedTagsUsed = false,
    aggregatedQualifications = [],
    isQualificationsLoading = false,
    transcriptMessages,
    callStatus = 'idle',
    onAcceptCall,
    onRejectCall,
    onHangupCall,
    isMuted: incomingIsMuted,
    onMuteToggle: incomingOnMuteToggle,
    isOnHold: incomingIsOnHold,
    onHoldToggle: incomingOnHoldToggle,
    isActive = true,
    statuses,
    callDetails,
}) => {
    // Determine if call is active (only 'in-progress' or 'incoming' should show call content)
    const isCallActive = callStatus === 'in-progress' || callStatus === 'incoming';

    return (
        <div className='agent-call-console'>
            <h2>Agent Call Console</h2>

            <CallingCard
                callStatus={callStatus}
                onAcceptCall={onAcceptCall}
                onRejectCall={onRejectCall}
                onHangupCall={onHangupCall}
                onMuteToggle={incomingOnMuteToggle}
                isMuted={incomingIsMuted}
                onHoldToggle={incomingOnHoldToggle}
                isOnHold={incomingIsOnHold}
                isActive={isActive}
            />

            {/* Only show call-related information when a call is active */}
            {isCallActive && (
                <>
                    {/* Client Info Card */}
                    {client && client.name && <ClientInfoCard client={client} />}

                    {/* Qualifications section only shows for in-progress calls */}
                    {callStatus === 'in-progress' &&
                        (aggregatedTagsUsed ? (
                            <AggregatedQualificationsAccordion
                                qualifications={aggregatedQualifications}
                                isLoading={isQualificationsLoading}
                            />
                        ) : (
                            qualifications?.length > 0 && <QualificationBadges qualifications={qualifications} />
                        ))}

                    {/* Call Transcript section only shows for in-progress calls */}
                    {transcriptMessages && transcriptMessages.length > 0 && callStatus === 'in-progress' && (
                        <CallTranscript messages={transcriptMessages} />
                    )}

                    {/* Update Contact Status section only shows for in-progress calls */}
                    {statuses && statuses.length > 0 && callDetails && callStatus === 'in-progress' && (
                        <div className='update-contact-section'>
                            <UpdateContactStatus statuses={statuses} contactId={callDetails?.id} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CallConsole;
